import './CommercialWithApplicationId.css';

import { parse } from 'qs';
import React, { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Model,
  QuestionMatrixDynamicModel,
  QuestionSelectBase
} from 'survey-core';

import Loading from '../../components/Loading/Loading';
import {
  useGetHideEoQuery,
  useGetJsoNbyApplicationIdQuery
} from '../../generated/graphql';
import { Canada, US } from '../../utils/address/province';
import { UnderwritingUrlData } from '../../utils/getUnderwritingUrlData';
import setMinMaxDateForEffectiveDate from '../../utils/setMinMaxDateForEffectiveDate';
import { getValueFromChoicesByLabel } from '../../utils/SurveyJSQuestion';
import { useFlags } from '../../utils/useFlags';
import CommercialSurveyWithApplicationId from './CommercialSurveyWithApplicationId';
import checkStateSupport from './supportedStates';
import { getAmtrustOfficerPanel } from './util';

interface CommercialSurveyWithApplicationIdParams {
  applicationId: string;
}

const CommercialWithApplicationId: React.FC = () => {
  const location = useLocation();
  const { group } = parse(location.search, {
    ignoreQueryPrefix: true
  });

  const {
    applicationId
  } = useParams<CommercialSurveyWithApplicationIdParams>();
  const {
    loading: isgetJSONbyApplicationIdLoading,
    error: getJSONbyApplicationIdError,
    data: getJSONbyApplicationIdData
  } = useGetJsoNbyApplicationIdQuery({
    variables: { applicationId }
  });

  // O(1) read of Configuration.hideEO; drives the EO Mock question's visibleIf.
  const { data: hideEOData, loading: isHideEOLoading } = useGetHideEoQuery();

  const { amTrustFullFlow } = useFlags();

  // Gate model setup on BOTH queries — otherwise whichever resolves second
  // (usually hideEO) misses the useMemo and its variable falls back stale.
  const canModelBeSetup = !!getJSONbyApplicationIdData && !!hideEOData;

  const model = useMemo(() => {
    if (!getJSONbyApplicationIdData) {
      return undefined;
    }

    const {
      getJSONbyApplicationId: {
        json,
        professionList,
        customerInfo,
        state,
        previousAnswers,
        wcData,
        policyStartDateStr,
        carrierPartner,
        displayWcQuestions
      }
    } = getJSONbyApplicationIdData;

    const model = new Model(json);

    // set default value of province/state — only available on warm-load
    // (history.push from Phase 1). On cold-load, location.state is undefined
    // and we skip the default-set (model still loads from getJSONbyApplicationId).
    const { province } =
      (location.state as AddressParams | undefined) ?? ({} as AddressParams);
    const provinceValue = US.isLongName(province)
      ? US.getShortName(province)
      : Canada.isLongName(province)
      ? Canada.getShortName(province)
      : undefined;
    if (provinceValue) {
      model.setValue('BusinessInformation_100_BusinessAddress_WORLD_EN', {
        province: provinceValue
      });
    }

    const isStateSupported =
      US.isLongName(state) && checkStateSupport(US.getShortName(state));

    // TODO add helper function
    model.setVariable('isStateSupported', isStateSupported);
    model.setVariable('amTrustMaster', displayWcQuestions); // only internal brokers can bind Amtrust policy
    model.setVariable('amTrustFullFlow', amTrustFullFlow && displayWcQuestions);
    model.setVariable('isEndorsement', false);
    model.setVariable('isBindOnlineAmtrust', false);
    model.setVariable('carrierPartner', carrierPartner);
    model.setVariable('hideEO', hideEOData?.hideEO ?? true);

    const { firstName, lastName } = customerInfo;

    model.setValue(
      'BusinessInformation_100_Profession_WORLD_EN',
      professionList
    );
    model.setValue(
      'BusinessInformation_100_CustomerInfo_WORLD_EN',
      customerInfo
    );
    const currentYear = new Date().getFullYear();
    model.setVariable('currentYear', currentYear);

    const stateQuestion = model.getQuestionByName(
      'BusinessInformation_100_Locations_US_EN'
    );
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (stateQuestion) {
      stateQuestion.title = `${stateQuestion.title} ${state}?`;
    }

    const stateLocationQuestion = model.getQuestionByName(
      'BusinessInformation_100_State_WORLD_EN'
    );

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (state && stateLocationQuestion) {
      const stateValue = getValueFromChoicesByLabel(
        state,
        stateLocationQuestion as QuestionSelectBase
      );

      model.setValue('BusinessInformation_100_State_WORLD_EN', stateValue);
    }

    const effectiveDateQuestion = model.getQuestionByName(
      'BusinessInformation_100_EffectiveDate_WORLD_EN'
    );

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (effectiveDateQuestion && !effectiveDateQuestion.visible) {
      // set effective date
      model.setValue(
        'BusinessInformation_100_EffectiveDate_WORLD_EN',
        policyStartDateStr
      );
    }

    // endorsement case so wcData will not be defined
    if (previousAnswers) {
      return model;
    }

    if (!wcData) {
      throw new Error('wcData should be defined');
    }

    switch (wcData.__typename) {
      case 'GetJsonWcSuccess': {
        model.data = {
          ...model.data,
          Amtrust_Officers: [
            {
              name: `${firstName} ${lastName}`
            }
          ]
        };

        const { classCodes, rules, isContractor } = wcData;

        const amTrustOfficers = getAmtrustOfficerPanel(model);

        model.setVariable('rules', rules);
        model.setVariable('isContractor', isContractor);
        model.setVariable('amtrustAPIFail', false);

        if (US.isLongName(state)) {
          model.setVariable('UsState', US.getShortName(state));
        }

        const officerClassCode = amTrustOfficers.template.getQuestionByName(
          'duties'
        ) as QuestionMatrixDynamicModel;

        const employeePayroll = model.getQuestionByName(
          'Amtrust_EmployeePayrollMatrix_US_EN'
        ) as QuestionMatrixDynamicModel;

        const cleanedWcClassCodes = classCodes.map(
          ({ id: { __typename, ...id }, description }) => {
            void __typename; // strip __typename
            return {
              text: description,
              value: id
            };
          }
        );
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (officerClassCode && employeePayroll) {
          officerClassCode.choices = employeePayroll.choices = cleanedWcClassCodes;
        }

        break;
      }
      case 'GetJsonWcError': {
        // switch to broker flow

        const wcDyanmicQuestionPage = model.getPageByName(
          'AmtrustWcAdditionalQuestions'
        );

        const brokerflowPage = model.getPageByName(
          'BrokerWorkersCompensation_0001'
        );

        wcDyanmicQuestionPage.visible = false;
        brokerflowPage.visible = true;

        model.setVariable('amtrustAPIFail', true);

        break;
      }
      case 'GetJsonWcUnavailable': {
        // don't inject anything
        break;
      }
      default:
        throw new Error('Unreachable - unknown wcData response type'); // try updating your GrpahQL schema
    }

    return model;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, canModelBeSetup]); // note: this dependency array means we can only run this twice per application (i.e. before data is ready and after data is ready)

  if (isgetJSONbyApplicationIdLoading || isHideEOLoading) {
    return <Loading />;
  }

  if (getJSONbyApplicationIdError || !getJSONbyApplicationIdData) {
    throw new Error(
      `${
        getJSONbyApplicationIdError
          ? 'getJSONbyApplicationId query error'
          : 'getJSONbyApplicationIdData should be ready'
      }`
    );
  }

  const {
    json,
    jsonFileName,
    previousAnswers,
    policyStartDateStr,
    policyExpiryDateStr,
    professionList,
    transactionType
  } = getJSONbyApplicationIdData.getJSONbyApplicationId;

  if (!json) {
    throw new Error('JSON is not ready before render the form');
  }

  if (!professionList) {
    throw new Error('Could not find the profession');
  }

  if (typeof group !== 'string' && group !== undefined) {
    throw new Error('Unreachable - group type error');
  }

  if (!model) {
    throw new Error('Unreachable - model should be ready at this stage');
  }

  // for endorsement
  if (previousAnswers) {
    const {
      BusinessInformation_100_EffectiveDate_WORLD_EN,
      BusinessInformation_100_Profession_WORLD_EN,
      BusinessInformation_100_CustomerInfo_WORLD_EN,
      BusinessInformation_100_State_WORLD_EN,
      ...previousAnswersWithoutFirstPageAnswers
    } = previousAnswers;

    // Void the unused variables
    void BusinessInformation_100_EffectiveDate_WORLD_EN;
    void BusinessInformation_100_Profession_WORLD_EN;
    void BusinessInformation_100_CustomerInfo_WORLD_EN;
    void BusinessInformation_100_State_WORLD_EN;

    // TODO find a better way to set this flag
    model.setVariable('isEndorsement', true);

    model.setDataCore({
      ...model.data,
      ...previousAnswersWithoutFirstPageAnswers
    });
  }

  setMinMaxDateForEffectiveDate(
    model,
    policyStartDateStr,
    policyExpiryDateStr,
    transactionType
  );

  // for endorsement
  if (previousAnswers) {
    model.runTriggers();
  }

  interface AddressParams {
    country: string;
    province: string;
    brokerCode?: string;
  }
  const { country, province, brokerCode } =
    (location.state as AddressParams | undefined) ?? ({} as AddressParams);
  const urlData =
    (location.state as UnderwritingUrlData | undefined) ??
    ({} as UnderwritingUrlData);
  return (
    <div className="h-full Commercial" data-testid="Commercial">
      <CommercialSurveyWithApplicationId
        model={model}
        applicationId={applicationId}
        jsonFileName={jsonFileName}
        groupName={group}
        country={country}
        province={province}
        urlData={urlData}
        brokerCode={brokerCode}
      />
    </div>
  );
};

export default CommercialWithApplicationId;
